/**
 * Workflow Diagram
 *
 * ┌───────────────────────────────┐
 * │   Redux: selectDistrictData   │
 * │   (district info: id, name,   │
 * │    logo_url, address)         │
 * └───────────┬───────────────────┘
 *             │
 *             ▼
 * ┌──────────────────────────────────────────────┐
 * │ useSidebarContext()                          │
 * │ → showSidebar (boolean: open/closed state)   │
 * │ → setSidebar (function: toggle sidebar)      │
 * └───────────┬──────────────────────────────────┘
 *             │
 *             ▼
 * ┌──────────────────────────────────────────────┐
 * │ DistrictInfo UI                              │
 * │  - Avatar (DynamicAvatar / Skeleton)         │
 * │  - District name + address (Typography)      │
 * │  - Collapse/Expand toggle (IconButton)       │
 * │  - Divider for separation                    │
 * └──────────────────────────────────────────────┘
 */

import React from "react";
import { Divider, IconButton, Skeleton, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DynamicAvatar, OverflowTooltip } from "Reuse/UI";
import { LeftWithBorderIcon } from "components/icon";
import { useSelector } from "react-redux";
import { selectDistrictData } from "store/slices";
import { useSidebarContext } from "../context/SidebarStateContext";
import { useNavigate } from "react-router";

/**
 * DistrictInfo Component
 * ----------------------
 * Displays district information (avatar, name, address) inside the sidebar.
 *
 * Props:
 * - onAvatarClick: callback triggered when avatar is clicked
 * - isMenuCollapse: boolean → whether sidebar is in collapsed mode
 *
 * State/Context:
 * - Redux → district info (id, name, logo_url, address)
 * - SidebarContext → manages sidebar open/close state
 */
const DistrictInfo = (props) => {
  const { t } = useTranslation("layout");
  const { onAvatarClick, isMenuCollapse = false } = props;
  const navigate = useNavigate();

  // Sidebar state (open/close + toggler)
  const { showSidebar, setSidebar } = useSidebarContext();

  // Fetch district data from Redux store
  const { id, name, address, logo_url } = useSelector(selectDistrictData);

  const theme = useTheme();

  return (
    <>
      <Stack
        direction="row"
        sx={{ p: 2, gap: 1, alignItems: "center", height: 84 }}
      >
        {/* Show avatar (DynamicAvatar if district exists, else Skeleton) */}
        {!isMenuCollapse ? (
          !!id ? (
            <DynamicAvatar
              sizes="small"
              onClick={showSidebar ? null : onAvatarClick}
              alt={name}
              src={logo_url}
              sx={{
                border: "1px solid #DBDDE0",
                cursor: showSidebar ? "default" : "pointer",
              }}
            />
          ) : (
            <Skeleton variant="circular" width={36} height={36} />
          )
        ) : null}

        {/* Show district details (name + address) if sidebar expanded or collapsed */}
        {showSidebar || isMenuCollapse ? (
          <Stack
            direction="column"
            sx={{
              gap: 0.5,
              flexGrow: 1,
              ...theme.mixins.overflow
            }}>
            <OverflowTooltip
              variant="body1"
              placement="bottom"
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: "16px",
                cursor: "pointer",
                "&:hover":{
                  fontWeight: theme.typography.fontWeightBold,

                }
              }}
              onClick={() => navigate(`/district/${id}`)}
            >
              {name ?? <Skeleton />}
            </OverflowTooltip>
            <OverflowTooltip
              variant="caption"
              placement="bottom"
              sx={() => ({
                color: theme.palette.text.secondary,
              })}
            >
              {address ?? <Skeleton />}
            </OverflowTooltip>
          </Stack>
        ) : null}

        {/* Collapse/Expand sidebar button */}
        {showSidebar || isMenuCollapse ? (
          <IconButton
            onClick={() => setSidebar(!showSidebar)}
            aria-label={showSidebar ? t("collapseSidebar") : t("expandSidebar")}
          >
            <LeftWithBorderIcon
              sx={{ transform: `rotate(${isMenuCollapse ? 180 : 0}deg)` }}
            />
          </IconButton>
        ) : null}
      </Stack>

      {/* Divider to separate this block from others */}
      <Divider variant="middle" />
    </>
  );
};

// Memoized to prevent unnecessary re-renders when props/store data don’t change
export default React.memo(DistrictInfo);
